/**
 * PgDiagnostic — interactive version of the Card 5 player-profile
 * worksheet. Replaces the static fillable form with real inputs that
 * persist per-coach in user_data.pg_diagnostics.
 *
 * UX:
 *   - First-time coach lands on the form with everything blank, ready
 *     to type a player name and start filling in.
 *   - Subsequent coaches see a list of their saved diagnostics at top
 *     (Player Name + age + "Edit" / "Delete"). "New Diagnostic" button
 *     opens a fresh form.
 *   - Save persists to user_data + closes the form. Auto-save on every
 *     input edit so a tab-close doesn't lose work.
 *   - Form fields are dropdowns where the masterclass already defined
 *     a finite set of answers (archetype, confidence), free text
 *     everywhere else.
 *
 * Storage:
 *   user_data.pg_diagnostics = {
 *     diagnostics: [
 *       { id, playerName, age, rateLimiters: [string, string, string],
 *         superpowersElite: [string],
 *         superpowersDeveloping: [string, string],
 *         archetype, confidence,
 *         learningDynamics: { frustration, autonomy, social, pacing, visual },
 *         next90Days: [string, string, string],
 *         createdAt, updatedAt }
 *     ]
 *   }
 */

import React, { useEffect, useMemo, useState } from "react";
import { Plus, Edit3, Trash2, Save as SaveIcon, X, FileText, ArrowLeft } from "lucide-react";
import { GOLD } from "../constants/theme";
import { loadUserData, saveUserData } from "../utils/userData";
import { bamConfirm } from "../shared/dialog";
import BamLoading from "../shared/BamLoading";
import { Select as PickerSelect } from "../shared/Pickers";

const ARCHETYPES = ["Quarterback", "Downhill Creator", "Shot-First", "Floor Architect", "Unsure / between two"];
const CONFIDENCE = ["low", "moderate", "high"];

const STORAGE_KEY = "pg_diagnostics";

function newDiagnostic() {
  return {
    id: `diag_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
    playerName: "",
    age: "",
    rateLimiters: ["", "", ""],
    superpowersElite: [""],
    superpowersDeveloping: ["", ""],
    archetype: "",
    confidence: "",
    learningDynamics: {
      frustration: "",
      autonomy: "",
      social: "",
      pacing: "",
      visual: "",
    },
    next90Days: ["", "", ""],
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
}

export function PgDiagnostic({ C }) {
  // List of saved diagnostics (loaded from user_data on mount)
  const [diagnostics, setDiagnostics] = useState([]);
  const [loading, setLoading] = useState(true);
  // The currently-open form. null = list view; an object = editing/creating.
  const [draft, setDraft] = useState(null);
  // Has the loaded list been persisted at least once? Used to decide
  // whether the autosave effect should write.
  const initial = useMemo(() => Object.create(null), []);

  useEffect(() => {
    let cancelled = false;
    // Watchdog: if the supabase round-trip stalls (cold start, transient
    // network, prefetch race), don't leave the coach on a spinner. Fall
    // through to the empty form after 3s — they can still type, and the
    // real load (if it eventually completes) will merge into state.
    const watchdog = setTimeout(() => {
      if (cancelled || initial.loaded) return;
      console.warn("[PgDiagnostic] load timed out; falling through to form");
      initial.loaded = true;
      setDraft(newDiagnostic());
      setLoading(false);
    }, 3000);

    loadUserData(STORAGE_KEY, { diagnostics: [] })
      .then(d => {
        if (cancelled) return;
        clearTimeout(watchdog);
        const list = Array.isArray(d?.diagnostics) ? d.diagnostics : [];
        setDiagnostics(list);
        // If the coach has no saved diagnostics yet, open the form
        // immediately so they don't see an empty list and bounce.
        if (list.length === 0) setDraft(newDiagnostic());
        initial.loaded = true;
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        clearTimeout(watchdog);
        console.warn("[PgDiagnostic] load failed:", err?.message || err);
        initial.loaded = true;
        setDraft(newDiagnostic());
        setLoading(false);
      });

    return () => { cancelled = true; clearTimeout(watchdog); };
    // eslint-disable-next-line
  }, []);

  // Autosave whenever the diagnostics list mutates after the initial load.
  useEffect(() => {
    if (!initial.loaded) return;
    saveUserData(STORAGE_KEY, { diagnostics }).catch(() => {});
    // eslint-disable-next-line
  }, [diagnostics]);

  const saveDraft = () => {
    if (!draft) return;
    const now = new Date().toISOString();
    setDiagnostics(prev => {
      const idx = prev.findIndex(d => d.id === draft.id);
      const next = { ...draft, updatedAt: now };
      if (idx >= 0) {
        const copy = [...prev];
        copy[idx] = next;
        return copy;
      }
      return [next, ...prev];
    });
    setDraft(null);
  };

  const cancelDraft = () => setDraft(null);

  const editDiagnostic = (id) => {
    const target = diagnostics.find(d => d.id === id);
    if (target) setDraft({ ...target });
  };

  const removeDiagnostic = async (id) => {
    if (!(await bamConfirm({ title: "Delete this diagnostic?", message: "This can't be undone.", confirmLabel: "Delete", danger: true }))) return;
    setDiagnostics(prev => prev.filter(d => d.id !== id));
  };

  const startNew = () => setDraft(newDiagnostic());

  if (loading) {
    return (
      <div style={shellStyle(C)}>
        <BamLoading pad={32} label="Loading your diagnostics…"/>
      </div>
    );
  }

  // FORM VIEW
  if (draft) {
    return (
      <div style={shellStyle(C)}>
        <DiagnosticHeader
          title={diagnostics.find(d => d.id === draft.id) ? "Edit diagnostic" : "New diagnostic"}
          rightContent={
            <button onClick={cancelDraft} style={ghostBtnStyle(C)}>
              <ArrowLeft size={12}/> Back
            </button>
          }
        />
        <DiagnosticForm draft={draft} setDraft={setDraft} C={C}/>
        <div style={{display:"flex",gap:10,padding:"14px 20px",borderTop:`1px solid ${GOLD}22`,background:"rgba(0,0,0,0.12)"}}>
          <button onClick={cancelDraft} style={{...ghostBtnStyle(C), flex:1, justifyContent:"center"}}>
            Cancel
          </button>
          <button onClick={saveDraft} style={{...goldBtnStyle, flex:1, justifyContent:"center"}}>
            <SaveIcon size={13}/> Save diagnostic
          </button>
        </div>
      </div>
    );
  }

  // LIST VIEW
  return (
    <div style={shellStyle(C)}>
      <DiagnosticHeader
        title="Your saved diagnostics"
        rightContent={
          <button onClick={startNew} style={goldBtnStyle}>
            <Plus size={12}/> New diagnostic
          </button>
        }
      />
      <div style={{padding:"18px 22px"}}>
        {diagnostics.length === 0 ? (
          <div style={{padding:"24px 0",textAlign:"center",color:C.textDim,fontSize:13}}>
            No saved diagnostics yet. Tap "New diagnostic" above to run one on a player.
          </div>
        ) : (
          <div style={{display:"flex",flexDirection:"column",gap:8}}>
            {diagnostics.map(d => (
              <div key={d.id} style={{
                display:"flex",alignItems:"center",gap:12,
                padding:"12px 14px", borderRadius:10,
                background: "rgba(255,255,255,0.025)",
                border: `1px solid ${C.cardBorder || C.border}`,
              }}>
                <div style={{flexShrink:0,width:34,height:34,borderRadius:8,background:`${GOLD}22`,border:`1px solid ${GOLD}55`,display:"flex",alignItems:"center",justifyContent:"center"}}>
                  <FileText size={16} color={GOLD}/>
                </div>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:14,fontWeight:700,color:C.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                    {d.playerName || "Untitled diagnostic"}
                  </div>
                  <div style={{fontSize:11,color:C.textDim,marginTop:2,display:"flex",gap:8,flexWrap:"wrap"}}>
                    {d.age && <span>Age {d.age}</span>}
                    {d.archetype && <span>· {d.archetype}</span>}
                    <span>· Updated {new Date(d.updatedAt).toLocaleDateString()}</span>
                  </div>
                </div>
                <button onClick={() => editDiagnostic(d.id)} style={iconBtnStyle(C)} title="Edit">
                  <Edit3 size={13}/>
                </button>
                <button onClick={() => removeDiagnostic(d.id)} style={{...iconBtnStyle(C), color:"#E0656A"}} title="Delete">
                  <Trash2 size={13}/>
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Form ───────────────────────────────────────────────────────────

function DiagnosticForm({ draft, setDraft, C }) {
  const set = (path, val) => {
    setDraft(prev => {
      const next = { ...prev };
      if (path.length === 1) next[path[0]] = val;
      else if (path.length === 2) next[path[0]] = { ...next[path[0]], [path[1]]: val };
      else if (path.length === 3 && typeof path[1] === "number") {
        const arr = [...(next[path[0]] || [])];
        arr[path[1]] = val;
        next[path[0]] = arr;
      }
      next.updatedAt = new Date().toISOString();
      return next;
    });
  };

  return (
    <div style={{padding:"18px 22px",display:"flex",flexDirection:"column",gap:18}}>
      {/* Player meta */}
      <Row>
        <Input label="Player name" value={draft.playerName} onChange={v => set(["playerName"], v)} C={C} placeholder="e.g. Marcus Johnson"/>
        <Input label="Age" value={draft.age} onChange={v => set(["age"], v)} C={C} placeholder="16" width={100}/>
      </Row>

      {/* Rate limiters */}
      <Section label="Rate limiters (in priority order)" C={C}>
        <Input label="1." value={draft.rateLimiters[0]} onChange={v => set(["rateLimiters", 0], v)} C={C} placeholder="e.g. Pressure handling"/>
        <Input label="2." value={draft.rateLimiters[1]} onChange={v => set(["rateLimiters", 1], v)} C={C} placeholder="e.g. Second-layer reads"/>
        <Input label="3." value={draft.rateLimiters[2]} onChange={v => set(["rateLimiters", 2], v)} C={C} placeholder="(optional)"/>
      </Section>

      {/* Superpowers */}
 <Section label="Superpowers: Elite" C={C}>
        <Input label="•" value={draft.superpowersElite[0]} onChange={v => set(["superpowersElite", 0], v)} C={C} placeholder="e.g. Shooting gravity"/>
      </Section>

 <Section label="Superpowers: Developing" C={C}>
        <Input label="•" value={draft.superpowersDeveloping[0]} onChange={v => set(["superpowersDeveloping", 0], v)} C={C}/>
        <Input label="•" value={draft.superpowersDeveloping[1]} onChange={v => set(["superpowersDeveloping", 1], v)} C={C}/>
      </Section>

      {/* Archetype */}
      <Section label="Likely archetype" C={C}>
        <Row>
          <Select label="Archetype" value={draft.archetype} onChange={v => set(["archetype"], v)} options={ARCHETYPES} C={C}/>
          <Select label="Confidence" value={draft.confidence} onChange={v => set(["confidence"], v)} options={CONFIDENCE} C={C} width={160}/>
        </Row>
      </Section>

      {/* Learning dynamics */}
      <Section label="Learning dynamics" C={C}>
        <Input label="Frustration response" value={draft.learningDynamics.frustration} onChange={v => set(["learningDynamics","frustration"], v)} C={C} placeholder="e.g. Shuts down under struggle"/>
        <Input label="Support vs. autonomy" value={draft.learningDynamics.autonomy} onChange={v => set(["learningDynamics","autonomy"], v)} C={C} placeholder="e.g. Reliant; needs constant feedback"/>
        <Input label="Social vs. isolated" value={draft.learningDynamics.social} onChange={v => set(["learningDynamics","social"], v)} C={C} placeholder="e.g. Sharper with peers around"/>
        <Input label="Pacing" value={draft.learningDynamics.pacing} onChange={v => set(["learningDynamics","pacing"], v)} C={C} placeholder="e.g. High tempo, hates pauses"/>
        <Input label="Visual processing" value={draft.learningDynamics.visual} onChange={v => set(["learningDynamics","visual"], v)} C={C} placeholder="e.g. Self-reflective; learns from own film"/>
      </Section>

      {/* Next 90 days */}
 <Section label="Next 90 days: primary focus" C={C}>
        <Input label="Address rate limiter" value={draft.next90Days[0]} onChange={v => set(["next90Days", 0], v)} C={C}/>
        <Input label="Develop superpower" value={draft.next90Days[1]} onChange={v => set(["next90Days", 1], v)} C={C}/>
        <Input label="Stretch toward archetype" value={draft.next90Days[2]} onChange={v => set(["next90Days", 2], v)} C={C}/>
      </Section>
    </div>
  );
}

// ── Layout helpers ─────────────────────────────────────────────────

function Section({ label, children, C }) {
  return (
    <div>
      <div style={{
        fontSize: 10, fontWeight: 800, color: GOLD,
        letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 10,
      }}>{label}</div>
      <div style={{display:"flex",flexDirection:"column",gap:10}}>{children}</div>
    </div>
  );
}

function Row({ children }) {
  return <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>{children}</div>;
}

function Input({ label, value, onChange, C, placeholder, width }) {
  return (
    <div style={{flex: width ? `0 0 ${width}px` : 1, minWidth: width || 160}}>
      <label style={labelStyle(C)}>{label}</label>
      <input
        type="text"
        value={value || ""}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder || ""}
        style={inputStyle(C)}
      />
    </div>
  );
}

function Select({ label, value, onChange, options, C, width }) {
  return (
    <div style={{flex: width ? `0 0 ${width}px` : 1, minWidth: width || 160}}>
      <label style={labelStyle(C)}>{label}</label>
      <PickerSelect
        value={value || ""}
        onChange={e => onChange(e.target.value)}
        style={{...inputStyle(C), appearance: "auto", paddingRight: 28}}
      >
        <option value="">Select…</option>
        {options.map(o => <option key={o} value={o}>{o}</option>)}
      </PickerSelect>
    </div>
  );
}

function DiagnosticHeader({ title, rightContent }) {
  return (
    <div style={{
      display: "flex", alignItems: "center", justifyContent: "space-between",
      padding: "12px 20px",
      borderBottom: `1px solid ${GOLD}22`,
      background: "linear-gradient(180deg, rgba(226,221,159,0.10) 0%, rgba(226,221,159,0.02) 100%)",
    }}>
      <div style={{
        fontSize: 11, fontWeight: 800, color: GOLD,
        letterSpacing: 1.5, textTransform: "uppercase",
      }}>{title}</div>
      {rightContent}
    </div>
  );
}

// ── Style tokens ───────────────────────────────────────────────────

const shellStyle = (C) => ({
  margin: "20px 0",
  borderRadius: 14,
  border: `1px solid ${GOLD}55`,
  background: `linear-gradient(180deg, rgba(226,221,159,0.05), rgba(226,221,159,0.01))`,
  overflow: "hidden",
});

const labelStyle = (C) => ({
  display: "block",
  fontSize: 10, fontWeight: 700,
  color: C.textDim || "#999",
  letterSpacing: 0.5, textTransform: "uppercase",
  marginBottom: 4,
});

const inputStyle = (C) => ({
  width: "100%",
  padding: "9px 12px",
  background: "rgba(255,255,255,0.04)",
  border: `1px solid ${C.border || "rgba(255,255,255,0.10)"}`,
  borderRadius: 8,
  color: C.text || "#F5F5F5",
  fontSize: 13,
  outline: "none",
  fontFamily: "'DM Sans', sans-serif",
  boxSizing: "border-box",
  transition: "border-color .15s ease",
});

const goldBtnStyle = {
  padding: "8px 14px",
  borderRadius: 8,
  border: "none",
  background: GOLD,
  color: "#111",
  fontSize: 12, fontWeight: 800,
  cursor: "pointer",
  display: "inline-flex", alignItems: "center", gap: 6,
  fontFamily: "'DM Sans', sans-serif",
};

const ghostBtnStyle = (C) => ({
  padding: "7px 12px",
  borderRadius: 8,
  background: "transparent",
  border: `1px solid ${C.border}`,
  color: C.textMid,
  fontSize: 12, fontWeight: 700,
  cursor: "pointer",
  display: "inline-flex", alignItems: "center", gap: 6,
  fontFamily: "'DM Sans', sans-serif",
});

const iconBtnStyle = (C) => ({
  width: 30, height: 30, borderRadius: 7,
  background: "transparent",
  border: `1px solid ${C.border}`,
  color: C.textMid,
  cursor: "pointer",
  display: "inline-flex", alignItems: "center", justifyContent: "center",
});

export default PgDiagnostic;
